import { Component, OnInit, effect, inject, DOCUMENT } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ThemeBuilderService, ALL_THEME_VARIABLES } from '../../_services/theme-builder.service';
import { ThemeService } from '../../_services/theme.service';
import { ThemeHighlightService } from '../../_services/theme-highlight.service';
import { environment } from '../../../environments/environment';
import { ThemeDownloadPreviewComponent } from '../theme-download-preview/theme-download-preview.component';

// ── Types used for the upload diff table ──────────────────────────────────────

interface ThemeVariables {
  [key: string]: string;
}

interface ThemeJson {
  name?: string;
  displayName?: string;
  description?: string;
  light?: ThemeVariables;
  dark?: ThemeVariables;
}

interface ThemeDeltaRow {
  variable: string;
  label: string;
  /** Current Json Theme value */
  before: string;
  /** Updated Theme value */
  after: string;
  /** Equals `after` only when before !== after, otherwise empty */
  changes: string;
  isNew: boolean;
  isRemoved: boolean;
  hasChange: boolean;
}

interface DropdownOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-theme-playground',
  imports: [FormsModule, ThemeDownloadPreviewComponent],
  templateUrl: './theme-playground.component.html',
  styleUrl: './theme-playground.component.css',
})
export class ThemePlaygroundComponent implements OnInit {
  public themeBuilder = inject(ThemeBuilderService);
  private themeService = inject(ThemeService);
  private themeHighlight = inject(ThemeHighlightService);
  private document = inject(DOCUMENT);

  isVisible: boolean = environment.enableThemeSwitcher === true;
  isExpanded: boolean = false;
  showActiveOnly: boolean = false;

  // ── Upload / delta state ─────────────────────────────────────────────────
  showDeltaPanel = false;
  deltaMode: 'light' | 'dark' = 'light';
  deltaRows: ThemeDeltaRow[] = [];
  uploadError: string | null = null;

  private pendingThemeJson: ThemeJson | null = null;
  private currentThemeSnapshot: ThemeJson | null = null;

  // ── Download preview state ────────────────────────────────────────────────
  showDownloadPanel = false;
  downloadPreviewMode: 'light' | 'dark' = 'light';
  downloadPreviewRows: ThemeDeltaRow[] = [];

  private downloadCurrentTheme: ThemeJson | null = null;

  // Dropdown options for specific variable types
  borderRadiusOptions: DropdownOption[] = [
    { label: 'None', value: '0' },
    { label: '2px', value: '2px' },
    { label: '4px', value: '4px' },
    { label: '6px', value: '6px' },
    { label: '8px', value: '8px' },
    { label: '10px', value: '10px' },
    { label: '12px', value: '12px' },
    { label: '14px', value: '14px' },
    { label: '16px', value: '16px' },
    { label: '20px', value: '20px' },
    { label: '24px', value: '24px' },
  ];

  shadowOptions: DropdownOption[] = [
    { label: 'None', value: 'none' },
    { label: 'Small', value: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' },
    { label: 'Medium', value: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' },
    { label: 'Large', value: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' },
    { label: 'X-Large', value: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' },
    { label: 'Elevated', value: '0 24px 60px rgba(0,0,0,.55)' },
    { label: 'Soft', value: '0 18px 45px rgba(15,23,42,.10)' },
    { label: 'CTA Pink', value: '0 10px 22px rgba(236,0,140,.30)' },
    { label: 'CTA Rose', value: '0 10px 22px rgba(190,24,93,.55)' },
    { label: 'Cart Green', value: '0 18px 35px rgba(22,163,74,.8)' },
    { label: 'CTA Rio', value: '0 18px 35px rgba(197,58,47,.25)' },
    { label: 'Ribbon', value: '0 4px 10px rgba(0,0,0,.4)' },
  ];

  blendModeOptions: DropdownOption[] = [
    { label: 'Normal', value: 'normal' },
    { label: 'Multiply', value: 'multiply' },
    { label: 'Screen', value: 'screen' },
    { label: 'Overlay', value: 'overlay' },
    { label: 'Darken', value: 'darken' },
    { label: 'Lighten', value: 'lighten' },
    { label: 'Color Dodge', value: 'color-dodge' },
    { label: 'Color Burn', value: 'color-burn' },
  ];

  opacityOptions: DropdownOption[] = [
    { label: '0%', value: '0' },
    { label: '10%', value: '.1' },
    { label: '20%', value: '.2' },
    { label: '30%', value: '.3' },
    { label: '40%', value: '.4' },
    { label: '50%', value: '.5' },
    { label: '60%', value: '.6' },
    { label: '70%', value: '.7' },
    { label: '80%', value: '.8' },
    { label: '85%', value: '.85' },
    { label: '90%', value: '.9' },
    { label: '95%', value: '.95' },
    { label: '100%', value: '1' },
  ];

  fontFamilyOptions: DropdownOption[] = [
    { label: 'System Default', value: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
    { label: 'Futura', value: '"Futura", "Futura PT", "Avenir Next", "Montserrat", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
    { label: 'Inter', value: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
    { label: 'Roboto', value: '"Roboto", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
    { label: 'Helvetica', value: '"Helvetica Neue", Helvetica, Arial, sans-serif' },
    { label: 'Georgia', value: 'Georgia, "Times New Roman", serif' },
  ];

  borderOptions: DropdownOption[] = [
    { label: 'None', value: 'none' },
    { label: '1px Solid Light', value: '1px solid rgba(148,163,184,.3)' },
    { label: '1px Solid Subtle', value: '1px solid rgba(148,163,184,.35)' },
    { label: '1px Solid Medium', value: '1px solid rgba(148,163,184,.5)' },
    { label: '2px Solid', value: '2px solid rgba(148,163,184,.5)' },
  ];

  constructor() {
    // React to playground activation/deactivation
    effect(() => {
      if (!this.themeBuilder.isPlaygroundActive()) {
        const currentThemeId = this.themeService.getCurrentThemeId();
        this.loadCurrentTheme();
      }
    });
  }

  ngOnInit(): void {
    // Load current theme on initialization
    this.loadCurrentTheme();

    // Subscribe to theme changes from theme-controls
    this.themeService.currentTheme$.subscribe(themeId => {
      // Reload the theme into the playground when theme changes
      this.loadCurrentTheme();
    });
  }

  async loadCurrentTheme(): Promise<void> {
    const currentThemeId = this.themeService.getCurrentThemeId();
    await this.themeBuilder.loadThemeIntoPlayground(currentThemeId);

    // Sync mode
    const isDark = this.themeService.isDarkMode();
    this.themeBuilder.currentMode.set(isDark ? 'dark' : 'light');
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;

    if (this.isExpanded && !this.themeBuilder.isPlaygroundActive()) {
      this.themeBuilder.activatePlayground();
    } else if (!this.isExpanded && this.themeBuilder.isPlaygroundActive()) {
      this.themeBuilder.deactivatePlayground();
    }
  }

  toggleActiveFilter(): void {
    this.showActiveOnly = !this.showActiveOnly;
  }

  onVariableChange(varName: string, event: Event): void {
    const value = (event.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value;
    this.themeBuilder.updateVariable(varName, value);
  }

  onModeChange(event: Event): void {
    const mode = (event.target as HTMLSelectElement).value as 'light' | 'dark';
    this.themeBuilder.switchMode(mode);
  }

  /** Handle focus on a theme variable input field */
  onFieldFocus(varName: string): void {
    this.themeHighlight.setHighlightedVariable(varName);
  }

  /** Handle blur on a theme variable input field */
  onFieldBlur(): void {
    this.themeHighlight.clearHighlight();
  }

  /** Open the pre-download preview dialog instead of downloading immediately */
  async openDownloadPreview(): Promise<void> {
    // Fetch the on-disk theme for the "Current Json Theme" column
    const currentThemeId = this.themeService.getCurrentThemeId();
    let onDiskTheme: ThemeJson = { light: {}, dark: {} };
    try {
      const baseUrl = this.document.baseURI || '/';
      const url = new URL(`assets/themes/${currentThemeId}.json`, baseUrl).toString();
      const res = await fetch(url, { cache: 'no-cache' });
      if (res.ok) onDiskTheme = await res.json();
    } catch {
      // Proceed with empty baseline (all playground values appear as new)
    }

    this.downloadCurrentTheme = onDiskTheme;
    this.downloadPreviewMode = this.themeBuilder.currentMode();
    this.rebuildDownloadDelta();
    this.showDownloadPanel = true;
  }

  onDownloadPreviewModeChange(mode: 'light' | 'dark'): void {
    this.downloadPreviewMode = mode;
    this.rebuildDownloadDelta();
  }

  /** Confirm: perform the actual download and close the panel */
  onConfirmDownload(): void {
    this.themeBuilder.downloadTheme();
    this.closeDownloadPanel();
  }

  onCancelDownload(): void {
    this.closeDownloadPanel();
  }

  getVariableValue(varName: string): string {
    const vars = this.themeBuilder.currentVariables();
    return vars[varName] || '';
  }

  getVariableLabel(varName: string): string {
    return varName.replace(/^--/, '').split('-').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  isColorVariable(varName: string): boolean {
    const colorKeywords = ['bg', 'color', 'text', 'primary', 'secondary', 'accent', 'success', 'border', 'surface', 'pill', 'chip', 'cta', 'filter', 'seg', 'ribbon'];
    return colorKeywords.some(keyword => varName.toLowerCase().includes(keyword)) &&
           !varName.includes('shadow') &&
           !varName.includes('radius') &&
           !varName.includes('gradient') &&
           !varName.includes('overlay') &&
           !varName.includes('family') &&
           !varName.includes('blend');
  }

  isMetadataField(varName: string): boolean {
    return false; // No metadata fields in the UI anymore
  }

  getFieldType(varName: string): 'color' | 'dropdown' | 'text' | 'gradient' {
    // Color fields
    if (this.isColorVariable(varName)) {
      return 'color';
    }

    // Dropdown fields
    if (varName.includes('radius')) return 'dropdown';
    if (varName.includes('shadow')) return 'dropdown';
    if (varName.includes('blend-mode')) return 'dropdown';
    if (varName.includes('opacity')) return 'dropdown';
    if (varName.includes('font-family')) return 'dropdown';
    if (varName.includes('border') && !this.isColorVariable(varName)) return 'dropdown';
    if (varName.includes('gradient')) return 'gradient';

    // Default to text for overlay and other fields
    return 'text';
  }

  getDropdownOptions(varName: string): DropdownOption[] {
    if (varName.includes('radius')) return this.borderRadiusOptions;
    if (varName.includes('shadow')) return this.shadowOptions;
    if (varName.includes('blend-mode')) return this.blendModeOptions;
    if (varName.includes('opacity')) return this.opacityOptions;
    if (varName.includes('font-family')) return this.fontFamilyOptions;
    if (varName.includes('border') && !this.isColorVariable(varName)) return this.borderOptions;

    return [];
  }

  // Extract hex color from rgba or complex color strings for color picker
  getColorPickerValue(varName: string): string {
    const value = this.getVariableValue(varName);

    // If it's already a hex color, return it
    if (value.match(/^#[0-9A-Fa-f]{6}$/)) {
      return value;
    }

    // If it's rgba, extract RGB and convert to hex (ignore alpha)
    const rgbaMatch = value.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbaMatch) {
      const r = parseInt(rgbaMatch[1]);
      const g = parseInt(rgbaMatch[2]);
      const b = parseInt(rgbaMatch[3]);
      return '#' + [r, g, b].map(x => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      }).join('');
    }

    // Default to black if we can't parse
    return '#000000';
  }

  onColorPickerChange(varName: string, event: Event): void {
    const hexColor = (event.target as HTMLInputElement).value;
    this.themeBuilder.updateVariable(varName, hexColor);
  }

  // Check if current value is custom (not in dropdown options)
  isCustomValue(varName: string): boolean {
    const value = this.getVariableValue(varName);
    if (!value) return false;

    const options = this.getDropdownOptions(varName);
    return !options.some(opt => opt.value === value);
  }

  // Get all variables sorted alphabetically
  getSortedVariables(): string[] {
    const cssVars = ALL_THEME_VARIABLES.filter(v => v.startsWith('--')).sort();

    if (this.showActiveOnly) {
      // Filter to only show variables with values
      const mode = this.themeBuilder.currentMode();
      const currentVars = this.themeBuilder.playgroundTheme()[mode];
      const activeCss = cssVars.filter(v => currentVars[v] && currentVars[v].trim() !== '');
      return activeCss;
    }

    return cssVars;
  }

  // Check if a variable is active (has a value)
  isActiveVariable(varName: string): boolean {
    const value = this.getVariableValue(varName);
    return !!(value && value.trim() !== '');
  }

  // ── Upload theme ──────────────────────────────────────────────────────────

  /** Trigger the hidden file input */
  onUploadClick(): void {
    const input = this.document.getElementById('themeUploadInput') as HTMLInputElement | null;
    input?.click();
  }

  /** Handle file selection: parse JSON, fetch current theme, build delta */
  async onFileSelected(event: Event): Promise<void> {
    this.uploadError = null;
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const uploaded = JSON.parse(text) as ThemeJson;

      if (!uploaded.light && !uploaded.dark) {
        this.uploadError = 'Invalid theme file – missing "light" / "dark" keys.';
        this.resetFileInput(event);
        return;
      }

      // Fetch the current on-disk theme for the "before" column
      const currentThemeId = this.themeService.getCurrentThemeId();
      let current: ThemeJson = { light: {}, dark: {} };
      try {
        const baseUrl = this.document.baseURI || '/';
        const url = new URL(`assets/themes/${currentThemeId}.json`, baseUrl).toString();
        const res = await fetch(url, { cache: 'no-cache' });
        if (res.ok) current = await res.json();
      } catch {
        // If the fetch fails, compare against empty (all vars appear as "new")
      }

      this.pendingThemeJson = uploaded;
      this.currentThemeSnapshot = current;
      this.deltaMode = 'light';
      this.rebuildDelta();
      this.showDeltaPanel = true;
    } catch {
      this.uploadError = 'Failed to parse the file. Ensure it is valid JSON.';
    }

    this.resetFileInput(event);
  }

  /** Switch the diff view between light and dark mode */
  onDeltaModeChange(mode: 'light' | 'dark'): void {
    this.deltaMode = mode;
    this.rebuildDelta();
  }

  /** Apply the uploaded theme into the playground */
  onConfirmUpload(): void {
    if (!this.pendingThemeJson) return;
    this.themeBuilder.loadThemeFromJsonObject(this.pendingThemeJson as Parameters<ThemeBuilderService['loadThemeFromJsonObject']>[0]);
    // Expand the playground so the user sees the applied theme
    if (!this.isExpanded) {
      this.isExpanded = true;
    }
    this.closeDeltaPanel();
  }

  /** Discard the upload */
  onCancelUpload(): void {
    this.closeDeltaPanel();
  }

  // ── Delta helpers ─────────────────────────────────────────────────────────

  private rebuildDelta(): void {
    if (!this.pendingThemeJson || !this.currentThemeSnapshot) {
      this.deltaRows = [];
      return;
    }

    const mode = this.deltaMode;
    const currentVars: ThemeVariables = (this.currentThemeSnapshot[mode] as ThemeVariables) ?? {};
    const uploadedVars: ThemeVariables = (this.pendingThemeJson[mode] as ThemeVariables) ?? {};

    // Union of all CSS variable names from both themes
    const allNames = new Set<string>([
      ...Object.keys(currentVars).filter(v => v.startsWith('--')),
      ...Object.keys(uploadedVars).filter(v => v.startsWith('--')),
    ]);

    this.deltaRows = Array.from(allNames)
      .map((varName): ThemeDeltaRow => {
        const before = currentVars[varName] ?? '';
        const after = uploadedVars[varName] ?? '';
        const hasChange = before !== after;
        return {
          variable: varName,
          label: this.formatDeltaLabel(varName),
          before,
          after,
          changes: hasChange ? after : '',
          isNew: !currentVars[varName] && !!uploadedVars[varName],
          isRemoved: !!currentVars[varName] && !uploadedVars[varName],
          hasChange,
        };
      })
      .sort((a, b) => {
        // Changed rows first, then alphabetical
        if (a.hasChange !== b.hasChange) return a.hasChange ? -1 : 1;
        return a.variable.localeCompare(b.variable);
      });
  }

  private formatDeltaLabel(varName: string): string {
    return varName
      .replace(/^--/, '')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  private closeDeltaPanel(): void {
    this.showDeltaPanel = false;
    this.pendingThemeJson = null;
    this.currentThemeSnapshot = null;
    this.deltaRows = [];
    this.uploadError = null;
  }

  private resetFileInput(event: Event): void {
    (event.target as HTMLInputElement).value = '';
  }

  private rebuildDownloadDelta(): void {
    if (!this.downloadCurrentTheme) {
      this.downloadPreviewRows = [];
      return;
    }

    const mode = this.downloadPreviewMode;
    const onDiskVars: ThemeVariables = (this.downloadCurrentTheme[mode] as ThemeVariables) ?? {};
    const playgroundVars: ThemeVariables = (this.themeBuilder.playgroundTheme()[mode] as ThemeVariables) ?? {};

    // Union of ALL known variable names plus anything extra in either source
    const allNames = new Set<string>([
      ...ALL_THEME_VARIABLES.filter(v => v.startsWith('--')),
      ...Object.keys(onDiskVars).filter(v => v.startsWith('--')),
      ...Object.keys(playgroundVars).filter(v => v.startsWith('--')),
    ]);

    this.downloadPreviewRows = Array.from(allNames)
      .map((varName): ThemeDeltaRow => {
        const before = onDiskVars[varName] ?? '';
        const after = playgroundVars[varName] ?? '';
        const hasChange = before !== after;
        return {
          variable: varName,
          label: this.formatDeltaLabel(varName),
          before,
          after,
          // "--" when unchanged so the cell is never empty
          changes: hasChange ? after : '--',
          isNew: !onDiskVars[varName] && !!playgroundVars[varName],
          isRemoved: !!onDiskVars[varName] && !playgroundVars[varName],
          hasChange,
        };
      })
      .sort((a, b) => {
        // Changed rows first, then alphabetical
        if (a.hasChange !== b.hasChange) return a.hasChange ? -1 : 1;
        return a.variable.localeCompare(b.variable);
      });
  }

  private closeDownloadPanel(): void {
    this.showDownloadPanel = false;
    this.downloadCurrentTheme = null;
    this.downloadPreviewRows = [];
  }

  /** Returns true when the value looks like a CSS colour (hex, rgb, hsl) */
  isColor(value: string): boolean {
    const v = value.trim();
    return (
      /^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(v) ||
      /^rgba?\s*\(/.test(v) ||
      /^hsla?\s*\(/.test(v)
    );
  }
}
